import type {
  AttendanceReport,
  Attendance,
  ChangeMyPhoneRequest,
  CheckInRequest,
  ConfirmEmailChangeRequest,
  CreateHasherRequest,
  CreateKennelRequest,
  CreateMembershipSchemeRequest,
  CreateSpecialEventRequest,
  CreateTrailRequest,
  GrantAccessRequest,
  Hasher,
  Kennel,
  Membership,
  MembershipPeriod,
  MembershipScheme,
  Payment,
  Role,
  RoleAssignment,
  RoleAuditRecord,
  Schedule,
  SetHasherPhoneRequest,
  StartEmailChangeRequest,
  Trail,
  TrailHistoryEntry,
  UpdateKennelRequest,
  UpdateMembershipSchemeRequest,
  UpdateMyProfileRequest,
  UpdateTrailDetailsRequest,
} from "@hash/shared";

// GET /schemes bundles each scheme with its currently purchasable periods
// (materialized lazily by that read — Story 10.4).
export type SchemeWithPeriods = MembershipScheme & {
  purchasable: MembershipPeriod[];
};

// Shape returned by GET /me/history (Story 13.2).
export interface MyHistoryEvent {
  trailId: string;
  eventType?: "trail" | "special";
  number?: number;
  // Special-event name (Milestone 9); absent for numbered trails.
  name?: string;
  date?: string;
  kennel?: string;
  title?: string;
  nameSnapshot: string;
  virgin: boolean;
  visitor: boolean;
  hare: boolean;
  checkedInAt: string;
}
export interface MyHistory {
  events: MyHistoryEvent[];
  memberships: Membership[];
  payments: Payment[];
  stats: {
    total: number;
    last12Months: number;
    virgins: number;
    hared: number;
    lastHaredDate?: string;
    perKennel: Record<string, number>;
  };
}

// Shape returned by GET /trails/:id — the check-in screen's whole dataset.
export interface TrailDetail {
  trail: Trail;
  kennel?: Kennel;
  attendance: Attendance[];
  hashers: Hasher[];
  payments: Payment[];
  memberships: Membership[];
}

export interface HasherListParams {
  kennelId?: string;
  since?: string;
  cursor?: string;
  limit?: number;
}

export interface KennelListParams {
  q?: string;
  cursor?: string;
  limit?: number;
}

function queryString(params: object): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}
import { getConfig, isDemoMode } from "../config";
import { getIdToken } from "../auth/cognito";
import { demoRequest } from "./demo/handlers";

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  // Demo mode: serve every call from the in-browser backend (client storage),
  // never touching the network. Same response shapes, same error format.
  if (isDemoMode()) return demoRequest<T>(path, init);
  const { apiUrl } = await getConfig();
  const token = await getIdToken();
  const res = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  hashers: {
    list: (params: HasherListParams = {}) =>
      request<{ hashers: Hasher[]; cursor?: string }>(
        `/hashers${queryString(params)}`
      ),
    recent: (weeks: number) =>
      request<{ hashers: Hasher[] }>(`/hashers/recent?weeks=${weeks}`),
    // A hasher's membership rows across kennels (Story 10.1). Backs the hasher
    // record's dues view; state is derived client-side from the periods.
    memberships: (id: string) =>
      request<{ hasher: Hasher; memberships: Membership[] }>(
        `/hashers/${encodeURIComponent(id)}/memberships`
      ),
    create: (body: CreateHasherRequest) =>
      request<Hasher>(`/hashers`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ deleted: true }>(`/hashers/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    // Edit a hasher's name / home kennel / visitor (Story 11.2). Misman+.
    update: (
      id: string,
      patch: {
        name?: string;
        displayName?: string;
        nerdName?: string;
        email?: string;
        homeKennelId?: string;
        visitor?: boolean;
      }
    ) =>
      request<Hasher>(`/hashers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    // Attach a phone to a lite hasher, or fix the typo on an unclaimed one
    // (Stories 2.1, 2.2). No OTP is sent.
    setPhone: (id: string, phone: string) =>
      request<{ hasherId: string; phone: string; accountState: "unclaimed" }>(
        `/hashers/${encodeURIComponent(id)}/phone`,
        {
          method: "POST",
          body: JSON.stringify({ phone } satisfies SetHasherPhoneRequest),
        }
      ),
  },
  // Self-service for the signed-in hasher.
  me: {
    get: () => request<Hasher>(`/me`),
    // Change my own number (Story 1.3). Throws on REAUTH_REQUIRED — the caller
    // re-verifies and retries.
    changePhone: (phone: string) =>
      request<{ hasherId: string; phone: string }>(`/me/phone`, {
        method: "POST",
        body: JSON.stringify({ phone } satisfies ChangeMyPhoneRequest),
      }),
    // Self check-in to an in-window event (Story 13.1). Returns what I owe.
    checkIn: (trailId: string) =>
      request<{ attendance: Attendance; owed: number }>(`/me/attendance`, {
        method: "POST",
        body: JSON.stringify({ trailId }),
      }),
    // My cross-kennel history (Story 13.2).
    history: () => request<MyHistory>(`/me/history`),
    // Edit my own freely-changeable metadata (nerd name). Email is verification-
    // gated (startEmailChange / changeEmail below), so it isn't here.
    updateProfile: (patch: UpdateMyProfileRequest) =>
      request<Hasher>(`/me`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    // Replace my email — step 1: send a code to the new address.
    startEmailChange: (email: string) =>
      request<{ sent: true }>(`/me/email/start`, {
        method: "POST",
        body: JSON.stringify({ email } satisfies StartEmailChangeRequest),
      }),
    // Step 2: confirm the code; the email is only stored on success.
    changeEmail: (email: string, code: string) =>
      request<Hasher>(`/me/email`, {
        method: "POST",
        body: JSON.stringify({
          email,
          code,
        } satisfies ConfirmEmailChangeRequest),
      }),
  },
  kennels: {
    list: (params: KennelListParams = {}) =>
      request<{ kennels: Kennel[]; cursor?: string }>(
        `/kennels${queryString(params)}`
      ),
    create: (body: CreateKennelRequest) =>
      request<Kennel>(`/kennels`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Edit door fee / membership price / metadata (Stories 9.1, 10.3). Misman+.
    update: (id: string, patch: UpdateKennelRequest) =>
      request<Kennel>(`/kennels/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    // Promote a reference kennel to managed (Story 3.3). Admin, one-way.
    promote: (id: string) =>
      request<Kennel>(`/kennels/${encodeURIComponent(id)}/promote`, {
        method: "POST",
      }),
    // Set the schedule + generate trails (Story 3.4). Returns the count made.
    setSchedule: (id: string, schedule: Schedule) =>
      request<{ schedule: Schedule; generated: number }>(
        `/kennels/${encodeURIComponent(id)}/schedule`,
        { method: "PUT", body: JSON.stringify(schedule) }
      ),
    // Extend the generation horizon (Story 3.5).
    extendHorizon: (id: string, months: number) =>
      request<{ generated: number }>(
        `/kennels/${encodeURIComponent(id)}/schedule/extend`,
        { method: "POST", body: JSON.stringify({ months }) }
      ),
    delete: (id: string) =>
      request<{ deleted: true }>(`/kennels/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    // Kennel-scoped misman roles (Story 11.3). Misman-of-kennel or admin;
    // the appointee must be a claimed hasher.
    listMisman: (id: string) =>
      request<{ misman: RoleAssignment[] }>(
        `/kennels/${encodeURIComponent(id)}/misman`
      ),
    grantMisman: (id: string, hasherId: string) =>
      request<RoleAssignment>(`/kennels/${encodeURIComponent(id)}/misman`, {
        method: "POST",
        body: JSON.stringify({ hasherId }),
      }),
    revokeMisman: (id: string, hasherId: string) =>
      request<{ revoked: true }>(
        `/kennels/${encodeURIComponent(id)}/misman/${encodeURIComponent(hasherId)}`,
        { method: "DELETE" }
      ),
  },
  memberships: {
    // Record a cash membership payment buying one period (Story 10.1). When
    // periodId is omitted the server picks the default (the open period
    // covering today, else the next upcoming one).
    record: (kennelId: string, hasherId: string, periodId?: string) =>
      request<{
        membership: Membership;
        payment: Payment;
        period: MembershipPeriod;
      }>(`/kennels/${encodeURIComponent(kennelId)}/memberships`, {
        method: "POST",
        body: JSON.stringify({ hasherId, periodId }),
      }),
  },
  schemes: {
    // Membership schemes (Stories 10.3/10.4). Listing also materializes any
    // period whose purchase window has opened.
    list: (kennelId?: string) =>
      request<{ schemes: SchemeWithPeriods[] }>(
        `/schemes${kennelId ? `?kennelId=${encodeURIComponent(kennelId)}` : ""}`
      ),
    create: (body: CreateMembershipSchemeRequest) =>
      request<MembershipScheme>(`/schemes`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (id: string, patch: UpdateMembershipSchemeRequest) =>
      request<MembershipScheme>(`/schemes/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },
  trails: {
    list: (from?: string, to?: string) => {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      return request<{ trails: Trail[] }>(`/trails?${params}`);
    },
    // Keyset pagination for the picker's "Load more": the next `limit` trails in
    // the tab's direction, resuming after the boundary trail the client holds.
    // `hasMore` says whether further pages exist.
    listPage: (opts: {
      direction: "upcoming" | "past";
      limit?: number;
      after?: { id: string; date: string };
    }) => {
      const params = new URLSearchParams({ direction: opts.direction });
      if (opts.limit) params.set("limit", String(opts.limit));
      if (opts.after) {
        params.set("afterId", opts.after.id);
        params.set("afterDate", opts.after.date);
      }
      return request<{ trails: Trail[]; hasMore: boolean }>(`/trails?${params}`);
    },
    // Returns the trail along with the attendance rows + hashers needed to
    // render the check-in screen — the trail META row and its attendance rows
    // share a partition, so the server pulls both in a single query.
    get: (id: string) =>
      request<TrailDetail>(`/trails/${encodeURIComponent(id)}`),
    create: (body: CreateTrailRequest) =>
      request<Trail>(`/trails`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Create a special event (Milestone 9). Shares the POST /trails route; the
    // server routes on the eventType discriminator in the body.
    createSpecial: (body: Omit<CreateSpecialEventRequest, "eventType">) =>
      request<Trail>(`/trails`, {
        method: "POST",
        body: JSON.stringify({ ...body, eventType: "special" }),
      }),
    // Save trail details with optimistic locking (Stories 7.1/7.2). On a stale
    // version the server replies 409 + { code: "VERSION_CONFLICT", current }.
    updateDetails: (id: string, body: UpdateTrailDetailsRequest) =>
      request<Trail>(`/trails/${encodeURIComponent(id)}/details`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // Edit a trail's date/time (Story 4.1), misman-only, strict ordering.
    update: (id: string, patch: Partial<Pick<Trail, "date" | "startAt">>) =>
      request<Trail>(`/trails/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    history: (id: string) =>
      request<{ history: TrailHistoryEntry[] }>(
        `/trails/${encodeURIComponent(id)}/history`
      ),
    // Hare assignment (Stories 5.1–5.4) and reset (5.5).
    assignHare: (id: string, hasherId: string) =>
      request<Trail>(`/trails/${encodeURIComponent(id)}/hares`, {
        method: "POST",
        body: JSON.stringify({ hasherId }),
      }),
    removeHare: (id: string, hasherId: string) =>
      request<Trail>(
        `/trails/${encodeURIComponent(id)}/hares/${encodeURIComponent(hasherId)}`,
        { method: "DELETE" }
      ),
    reset: (id: string) =>
      request<Trail>(`/trails/${encodeURIComponent(id)}/reset`, {
        method: "POST",
      }),
    delete: (id: string) =>
      request<{ deleted: true; renumbered: number }>(
        `/trails/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
        }
      ),
  },
  attendance: {
    checkIn: (trailId: string, body: CheckInRequest) =>
      request<Attendance>(
        `/trails/${encodeURIComponent(trailId)}/attendance`,
        { method: "POST", body: JSON.stringify(body) }
      ),
    setPaid: (trailId: string, hasherId: string, paid: boolean) =>
      request<Attendance>(
        `/trails/${encodeURIComponent(trailId)}/attendance/${encodeURIComponent(hasherId)}`,
        { method: "PATCH", body: JSON.stringify({ paid }) }
      ),
    // Edit attendance attributes (Story 8.8): any of virgin/visitor/paid or the
    // name snapshot ("update to current name").
    update: (
      trailId: string,
      hasherId: string,
      patch: Partial<
        Pick<Attendance, "paid" | "virgin" | "visitor" | "nameSnapshot">
      >
    ) =>
      request<Attendance>(
        `/trails/${encodeURIComponent(trailId)}/attendance/${encodeURIComponent(hasherId)}`,
        { method: "PATCH", body: JSON.stringify(patch) }
      ),
    remove: (trailId: string, hasherId: string) =>
      request<{ removed: true }>(
        `/trails/${encodeURIComponent(trailId)}/attendance/${encodeURIComponent(hasherId)}`,
        { method: "DELETE" }
      ),
  },
  admin: {
    grantAccess: (body: GrantAccessRequest) =>
      request<unknown>(`/admin/access`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    changeRole: (hasherId: string, role: Role) =>
      request<unknown>(
        `/admin/hashers/${encodeURIComponent(hasherId)}/role`,
        { method: "PATCH", body: JSON.stringify({ role }) }
      ),
    // Reverse-chronological role-change audit (Story 12.4). Admin-only.
    roleAudit: (cursor?: string) =>
      request<{ records: RoleAuditRecord[]; cursor?: string }>(
        `/admin/role-audit${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`
      ),
  },
  reports: {
    // Per-kennel date-range attendance + payment summary (Story 9.3). Admin or
    // misman of every kennel requested; the window is inclusive (YYYY-MM-DD).
    attendance: (kennelIds: string[], from: string, to: string) =>
      request<AttendanceReport>(
        `/reports/attendance${queryString({
          kennelIds: kennelIds.join(","),
          from,
          to,
        })}`
      ),
  },
};
